import {
    context,
    diag,

    Span,
    Context,
    TraceFlags
} from '@opentelemetry/api';
import {
    BindOnceFuture,
    ExportResult,
    ExportResultCode,
    getEnv,
    globalErrorHandler,
    suppressTracing,
    unsuppressTracing,
    unrefTimer

} from '@opentelemetry/core';
import {SpanExporter, SpanProcessor,ReadableSpan} from '@opentelemetry/sdk-trace-web';

interface BatchSpanProcessorConfig {
    maxExportBatchSize?: number;
    maxQueueSize?: number;
    scheduledDelayMillis?: number;
    exportTimeoutMillis?: number;
}

/**
 * Implementation of the {@link SpanProcessor} that batches spans exported by
 * the SDK then pushes them to the exporter pipeline.
 */
export abstract class BatchSpanProcessorBase<T extends SpanExporter> implements SpanProcessor {
    private _finishedSpans: ReadableSpan[] = [];
    private _timer?: NodeJS.Timeout;
    private _isExporting: boolean = false;
    private _droppedSpansCount: number = 0;
    protected _shutdownOnce: BindOnceFuture<void>;

    private readonly _maxExportBatchSize: number;
    private readonly _maxQueueSize: number;
    private readonly _scheduledDelayMillis: number;
    private readonly _exportTimeoutMillis: number;

    constructor(protected readonly _exporter: T, config?: BatchSpanProcessorConfig) {
        const env = getEnv();
        this._maxExportBatchSize = config?.maxExportBatchSize ?? env.OTEL_BSP_MAX_EXPORT_BATCH_SIZE;
        this._maxQueueSize = config?.maxQueueSize ?? env.OTEL_BSP_MAX_QUEUE_SIZE;
        this._scheduledDelayMillis = config?.scheduledDelayMillis ?? env.OTEL_BSP_SCHEDULE_DELAY;
        this._exportTimeoutMillis = config?.exportTimeoutMillis ?? env.OTEL_BSP_EXPORT_TIMEOUT;

        this._shutdownOnce = new BindOnceFuture(this._shutdown, this);

        if (this._maxExportBatchSize > this._maxQueueSize) {
            diag.warn('BatchSpanProcessor: maxExportBatchSize must be smaller or equal to maxQueueSize, setting maxExportBatchSize to match maxQueueSize');
            this._maxExportBatchSize = this._maxQueueSize;
        }
    }

    forceFlush(): Promise<void> {
        // console.log("forcing flush");
        if (this._shutdownOnce.isCalled) {
            return this._shutdownOnce.promise;
        }
        return this._flushAll();
    }

    // does nothing.
    onStart(_span: Span, _parentContext: Context): void {
    }

    onEnd(span: ReadableSpan): void {
        if (this._shutdownOnce.isCalled) {
            return;
        }
        if ((span.spanContext().traceFlags & TraceFlags.SAMPLED) === 0) {
            return;
        }
        this._addToBuffer(span);
    }

    shutdown(): Promise<void> {
        return this._shutdownOnce.call();
    }

    private _shutdown(): Promise<void> {
        return Promise.resolve()
            .then(() => {
                return this.onShutdown();
            })
            .then(() => {
                return this._flushAll();
            })
            .then(() => {
                return this._exporter.shutdown();
            });
    }

    /** Add a span to the buffer. */
// Correct the type in method signatures that deal with finished spans
    private _addToBuffer(span: ReadableSpan): void {
        if (this._finishedSpans.length >= this._maxQueueSize) {
            if (this._droppedSpansCount === 0) {
                diag.debug('maxQueueSize reached, dropping spans');
            }
            this._droppedSpansCount++;
            return;
        }

        if (this._droppedSpansCount > 0) {
            diag.warn(`Dropped ${this._droppedSpansCount} spans because maxQueueSize reached`);
            this._droppedSpansCount = 0;
        }

        // Since _finishedSpans is an array of Span, not ReadableSpan, you might need to adjust its type
        // or ensure you're only storing the necessary data from ReadableSpan that matches Span's structure.
        this._finishedSpans.push(span);
        this._maybeStartTimer();
    }

    private _flushAll(): Promise<void> {
        // console.log("flushing all");
        return new Promise<void>((resolve, reject) => {
            const promises: Promise<void>[] = [];
            const count = Math.ceil(this._finishedSpans.length / this._maxExportBatchSize);
            for (let i = 0; i < count; i++) {
                promises.push(this._flushOneBatch());
            }
            Promise.all(promises).then(() => resolve()).catch(reject);
        });
    }

    private _flushOneBatch(): Promise<void> {
        this._clearTimer();
        if (this._finishedSpans.length === 0) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error('Timeout'));
            }, this._exportTimeoutMillis);
            // console.log("supressing tracing");
            let ctx = context.active();
            context.with(suppressTracing(ctx), () => {
            // context.with(context.active(), () => {
                let spans: ReadableSpan[];
                if (this._finishedSpans.length <= this._maxExportBatchSize) {
                    spans = this._finishedSpans;
                    this._finishedSpans = [];
                } else {
                    spans = this._finishedSpans.splice(0, this._maxExportBatchSize);
                }

                const doExport = (): void => {
                    this._exporter.export(spans, (result) => {
                        clearTimeout(timer);
                        if (result.code === ExportResultCode.SUCCESS) {
                            resolve();
                        } else {
                            reject(result.error ?? new Error('BatchSpanProcessor: span export failed'));
                        }
                    });
                };

                const pendingResources: Promise<void>[] = [];
                for (const span of spans) {
                    if (span.resource.asyncAttributesPending && span.resource.waitForAsyncAttributes) {
                        pendingResources.push(span.resource.waitForAsyncAttributes());
                    }
                }

                if (pendingResources.length === 0) {
                    // console.log("no pending resources");
                    doExport();
                } else {
                    // console.log("waiting for pending resources");
                    Promise.all(pendingResources).then(doExport, (err) => {
                        globalErrorHandler(err);
                        reject(err);
                        // context.with(unsuppressTracing(context.active()), () => {});
                    });
                }

            });
            context.with(unsuppressTracing(ctx), () => {
                // console.log("unsupressing tracing");
            });
        });

    }

    private _maybeStartTimer(): void {
        if (this._isExporting) return;

        const flush = (): void => {
            this._isExporting = true;
            this._flushOneBatch()
                .finally(() => {
                    this._isExporting = false;
                    if (this._finishedSpans.length > 0) {
                        this._clearTimer();
                        this._maybeStartTimer();
                    }
                })
                .catch((e) => {
                    this._isExporting = false;
                    globalErrorHandler(e);
                });
        };

        if (this._finishedSpans.length >= this._maxExportBatchSize) {
            return flush();
        }

        if (this._timer !== undefined) return;

        this._timer = setTimeout(() => flush(), this._scheduledDelayMillis);
        unrefTimer(this._timer);
    }

    private _clearTimer(): void {
        if (this._timer !== undefined) {
            clearTimeout(this._timer);
            this._timer = undefined;
        }
    }

    abstract onShutdown(): Promise<void> | void;
}
